import type { ReactNode } from "react"
import Select from "@/v2-ui/select"
import { useLicenseProductsQuery } from "@/v2-backoffice-client/license/license.products.query"
import type { LicenseProductsQuery } from "@/v2-backoffice-client/license/__types__/license.products.query"

export type LicenseProduct = LicenseProductsQuery["getLicenseProducts"][number]

export type LicenseProductSelectProps = {
  value: string
  /** The picked product rides along (undefined for an extra leading item). */
  onValueChange(value: string, product: LicenseProduct | undefined): void
  /** Extra leading items (e.g. the license board's "not mapped" sentinel). */
  children?: ReactNode
}

// The product catalog as a select, each product with its catalog sentence
// under it in the open list (for a seated product that sentence says its
// seats are assigned to named people — which is why the grant dialog then
// offers the holders). Owns its own catalog query, so the dialogs composing
// it (grant license, Update License) stay one-operation components. Mount it
// inside the dialog content: it only fetches when shown.
function LicenseProductSelect(props: LicenseProductSelectProps) {
  const { value, onValueChange, children } = props

  const { data, loading } = useLicenseProductsQuery()
  const products = data?.getLicenseProducts ?? []

  return (
    <Select
      value={value}
      onValueChange={(next) => {
        onValueChange(
          next,
          products.find((product) => String(product.Code) === next),
        )
      }}
    >
      <Select.Trigger size="md" className="w-full">
        <Select.Value
          placeholder={loading ? "Loading products..." : "Pick a product"}
        />
      </Select.Trigger>
      <Select.Content className="max-w-[440px]">
        {children}
        {products.map((product) => (
          <Select.Item
            key={product.Code}
            value={String(product.Code)}
            description={product.Description}
          >
            {product.Name}
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  )
}

export default LicenseProductSelect
